import axios from 'axios';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { getApiUrl, getImageUrl } from '../../utils/config';
import { formatPrice } from '../../utils/currency';
import LoginModal from './LoginModal';
import RegisterModal from './RegisterModal';

const CartModal = ({ storeId, onClose, onCartUpdate, store }) => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [loadingStates, setLoadingStates] = useState({});

  useEffect(() => {
    checkAuthStatus();
    if (isLoggedIn) {
      fetchCart();
    } else {
      loadLocalCart();
    }
  }, [storeId, isLoggedIn]);

  const checkAuthStatus = () => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
  };

  const fetchCart = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(getApiUrl(`/cart/${storeId}`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCartItems(response.data.data?.items || []);
    } catch (error) {
      console.error('Error fetching cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadLocalCart = () => {
    const localCart = localStorage.getItem(`cart_${storeId}`);
    if (localCart) {
      setCartItems(JSON.parse(localCart));
    }
    setLoading(false);
  };

  // تحديث البادج بعد كل عملية
  const updateCartBadge = () => {
    if (onCartUpdate) onCartUpdate();
  };

  const updateQuantity = async (itemId, newQuantity) => {
    if (newQuantity < 1) return;

    // Set loading state for this specific item
    setLoadingStates(prev => ({ ...prev, [`quantity_${itemId}`]: true }));

    if (isLoggedIn) {
      try {
        const token = localStorage.getItem('token');
        await axios.put(getApiUrl(`/cart/item/${itemId}`), {
          quantity: newQuantity
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchCart();
        toast.success('Quantity updated successfully!', {
          autoClose: 1000,
        });
        updateCartBadge();
      } catch (error) {
        console.error('Error updating cart item:', error);
      }
    } else {
      const updatedItems = cartItems.map(item =>
        item.id === itemId ? { ...item, quantity: newQuantity } : item
      );
      setCartItems(updatedItems);
      localStorage.setItem(`cart_${storeId}`, JSON.stringify(updatedItems));
      toast.success('Quantity updated successfully!', {
        autoClose: 1000,
      });
      updateCartBadge();
    }
    
    // Clear loading state
    setLoadingStates(prev => ({ ...prev, [`quantity_${itemId}`]: false }));
  };

  const removeItem = async (itemId) => {
    // Set loading state for this specific item
    setLoadingStates(prev => ({ ...prev, [`remove_${itemId}`]: true }));

    if (isLoggedIn) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(getApiUrl(`/cart/item/${itemId}`), {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchCart();
        toast.success('Item removed from cart', {
          autoClose: 1000,
        });
        updateCartBadge();
      } catch (error) {
        console.error('Error removing cart item:', error);
      }
    } else {
      const updatedItems = cartItems.filter(item => item.id !== itemId);
      setCartItems(updatedItems);
      localStorage.setItem(`cart_${storeId}`, JSON.stringify(updatedItems));
      toast.success('Item removed from cart', {
        autoClose: 1000,
      });
      updateCartBadge();
    }
    
    // Clear loading state
    setLoadingStates(prev => ({ ...prev, [`remove_${itemId}`]: false }));
  };

  const clearCart = async () => {
    // Set loading state for clear cart
    setLoadingStates(prev => ({ ...prev, clearCart: true }));

    if (isLoggedIn) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(getApiUrl(`/cart/${storeId}/clear`), {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCartItems([]);
        toast.success('Cart cleared', {
          autoClose: 1000,
        });
        updateCartBadge();
      } catch (error) {
        console.error('Error clearing cart:', error);
      }
    } else {
      setCartItems([]);
      localStorage.removeItem(`cart_${storeId}`);
      toast.success('Cart cleared', {
        autoClose: 1000,
      });
      updateCartBadge();
    }
    
    // Clear loading state
    setLoadingStates(prev => ({ ...prev, clearCart: false }));
  };

  // السعر بعد الخصم فقط
  const getDiscountedPrice = (price, discount) => {
    price = parseFloat(price) || 0;
    discount = parseFloat(discount) || 0;
    if (discount > 0) {
      return price - (price * discount / 100);
    }
    return price;
  };

  const calculateTotal = () => {
    return cartItems.reduce((total, item) => {
      const product = item.product || item;
      const price = getDiscountedPrice(product.price, product.discount_percentage);
      const quantity = item.quantity || 1;
      return total + (price * quantity);
    }, 0);
  };

  const handleCheckout = () => {
    if (!isLoggedIn) {
      // إذا لم يكن مسجل الدخول، افتح موديل تسجيل الدخول
      setShowLogin(true);
      return;
    }

    // إذا كان مسجل الدخول، افتح موديل الـ checkout
    setShowCheckout(true);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity duration-500"
        onClick={onClose}
      ></div>

      {/* Modal - Full height, not full width, slides from right */}
      <div className="absolute inset-y-0 right-0 w-80 sm:w-96 bg-white shadow-2xl transform transition-transform duration-700 ease-out">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Shopping Cart</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
              </div>
            ) : cartItems.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-gray-400 mb-4">
                  <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle cx="9" cy="21" r="1" />
                    <circle cx="20" cy="21" r="1" />
                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                  </svg>

                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Your cart is empty</h3>
                <p className="text-gray-500">Add some products to get started</p>
              </div>
            ) : (
              <div className="space-y-4">
                {cartItems.map((item) => {
                  const product = item.product || item;
                  const productName = product.name;
                  const productImage = product.image;
                  const productDiscount = product.discount_percentage;
                  const productPrice = parseFloat(product.price) || 0;
                  const discountedPrice = getDiscountedPrice(product.price, productDiscount);
                  const itemQuantity = item.quantity;
                  const itemId = item.id;
                  return (
                    <div key={itemId} className="flex items-center space-x-3 bg-gray-50 rounded-lg">
                      <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                        {productImage ? (
                          <img
                            src={getImageUrl(productImage)}
                            alt={productName}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        )}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-sm font-medium text-gray-900">{productName}</h3>
                        <div className="flex items-center space-x-2 mt-1">
                          {parseFloat(productDiscount) > 0 ? (
                            <>
                              <span className="text-sm text-gray-500 line-through">
                                {formatPrice(productPrice, store?.currency)}
                              </span>
                              <span className="text-sm font-medium text-green-600">
                                {formatPrice(discountedPrice, store?.currency)}
                              </span>
                            </>
                          ) : (
                            <span className="text-sm font-medium text-gray-900">
                              {formatPrice(productPrice, store?.currency)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {itemQuantity > 1 ? (
                          <button
                            onClick={() => updateQuantity(itemId, itemQuantity - 1)}
                            disabled={loadingStates[`quantity_${itemId}`]}
                            className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {loadingStates[`quantity_${itemId}`] ? (
                              <div className="animate-spin rounded-full h-3 w-3 border-b border-gray-600"></div>
                            ) : (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                              </svg>
                            )}
                          </button>
                        ) : (
                          <button
                            onClick={() => removeItem(itemId)}
                            disabled={loadingStates[`remove_${itemId}`]}
                            className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center hover:bg-red-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {loadingStates[`remove_${itemId}`] ? (
                              <div className="animate-spin rounded-full h-3 w-3 border-b border-red-600"></div>
                            ) : (
                              <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            )}
                          </button>
                        )}
                        <span className="text-sm font-medium w-8 text-center">{itemQuantity}</span>
                        <button
                          onClick={() => updateQuantity(itemId, itemQuantity + 1)}
                          disabled={loadingStates[`quantity_${itemId}`]}
                          className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {loadingStates[`quantity_${itemId}`] ? (
                            <div className="animate-spin rounded-full h-3 w-3 border-b border-gray-600"></div>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          {cartItems.length > 0 && (
            <div className="border-t border-gray-200 p-4">
              <div className="flex items-center justify-between mb-4">
                <span className="text-lg font-semibold text-gray-900">Total:</span>
                <span className="text-lg font-semibold text-gray-900">{formatPrice(calculateTotal(), store?.currency)}</span>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={clearCart}
                  disabled={loadingStates.clearCart}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {loadingStates.clearCart ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b border-gray-600 mr-2"></div>
                  ) : null}
                  Clear Cart
                </button>
                <button
                  onClick={handleCheckout}
                  className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                >
                  {isLoggedIn ? 'Checkout' : 'Login to Checkout'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showLogin && (
        <LoginModal
          onClose={() => setShowLogin(false)}
          onRegisterClick={() => {
            setShowLogin(false);
            setShowRegister(true);
          }}
          onSuccess={() => {
            setShowLogin(false);
            // After successful login, open checkout modal
            setShowCheckout(true);
          }}
        />
      )}

      {showRegister && (
        <RegisterModal
          onClose={() => setShowRegister(false)}
          onLoginClick={() => {
            setShowRegister(false);
            setShowLogin(true);
          }}
          onSuccess={() => {
            setShowRegister(false);
            // After successful registration, open checkout modal
            setShowCheckout(true);
          }}
        />
      )}

      {showCheckout && (
        <CheckoutModal
          storeId={storeId}
          total={calculateTotal()}
          onClose={() => setShowCheckout(false)}
          onSuccess={() => {
            setShowCheckout(false);
            onClose();
            updateCartBadge();
          }}
          store={store}
        />
      )}
    </div>
  );
};

// Checkout Modal Component
const CheckoutModal = ({ storeId, total, onClose, onSuccess, store }) => {
  const [address, setAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(getApiUrl(`/orders/${storeId}`), {
        notes: notes,
        address: address,
        payment_method: 'cash'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success('Order placed successfully!', {
          autoClose: 1000,
        });
        onSuccess();
      }
    } catch (error) {
      console.error('Error placing order:', error);
      toast.error('Failed to place order. Please try again.', {
        autoClose: 1000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity duration-500"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Complete Order</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                Delivery Address
              </label>
              <input
                type="text"
                id="address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Enter your delivery address..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                rows="3"
                maxLength="255"
              />
            </div>

            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                Order Notes (Optional)
              </label>
              <input
                type="text"
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any special instructions or notes..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                rows="3"
                maxLength="500"
              />
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-lg font-semibold text-gray-900">Total Amount:</span>
                <span className="text-lg font-semibold text-gray-900">{formatPrice(total, store?.currency)}</span>
              </div>
              <div className="mt-2 text-sm text-gray-600">
                Payment Method: Cash on Delivery
              </div>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Placing Order...' : 'Place Order'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CartModal; 